import { useTranslation } from "react-i18next";
import { MobileCardRow, MobileCardWrapper, TableBody, TableColumn, TableHeader, TableRow, TableCell } from "./dashboard/table-components";
import { ArrowLeftIcon, ArrowRightIcon } from "../assets/dashboard-icon.svg";
import { useRef, useState, useEffect } from "react";
import { usePensions } from "../hooks/usePensions";
import { PensionRecord } from "../api/pensions";
import { formatAmount, formatDateForPayout } from "../helper/common.helper";
import PensionSidebar from "./pension-sidebar.component";
import { getItem } from "../helper/localstorage.helper";
import { toast } from "react-toastify";
import CalendarSingle from "./calendar/calendar-single.component";
import "./holiday-pay-calendar.scss";
import LoadingCard from '../components/SkeletonLoading'

export const PensionReportSection = ({
    className,
}: {
    className?: string
}
) => {
    const { t } = useTranslation();
    const fromCalendarRef = useRef<HTMLDivElement>(null);
    const toCalendarRef = useRef<HTMLDivElement>(null);
    const isMobile = window.innerWidth <= 768;
    const [showSidebar, setShowSidebar] = useState<boolean>(true);
    const [fromDate, setFromDate] = useState<Date | null>(null);
    const [toDate, setToDate] = useState<Date | null>(null);
    const [status, setStatus] = useState<string>('');
    const [showFromCalendar, setShowFromCalendar] = useState<boolean>(false);
    const [showToCalendar, setShowToCalendar] = useState<boolean>(false);


    const [toolTipError, setToolTipError] = useState({
        invalidFromdate: false,
        toDateNotSelected: false,
        fromDateNotSelected: false,
    });

    const {
        pensionRecords,
        paginatedPensionRecords,
        totalPages,
        currentPage,
        searchQuery,
        setCurrentPage,
        setSearchQuery,
        goToPreviousPage,
        goToNextPage,
        loading,
        error,
        refetch
    } = usePensions({ pageSize: 10, fromDate, toDate });
    const language = getItem("i18nextLng") || 'en-us';

    useEffect(() => {
        refetch();
    }, [fromDate, toDate, status, currentPage]);

    // Auto-clear error tooltips after 6 seconds
    useEffect(() => {
        if (toolTipError.invalidFromdate || toolTipError.toDateNotSelected || toolTipError.fromDateNotSelected) {
            const timer = setTimeout(() => {
                setToolTipError({
                    invalidFromdate: false,
                    toDateNotSelected: false,
                    fromDateNotSelected: false,
                });
            }, 6000);

            return () => clearTimeout(timer);
        }
    }, [toolTipError]);


    const handleClearFilters = () => {
        try {
            setFromDate(null);
            setToDate(null);
            setStatus('');
            setCurrentPage(1);
            setToolTipError({
                invalidFromdate: false,
                toDateNotSelected: false,
                fromDateNotSelected: false,
            });
        } catch (error) {
            console.error('Error clearing filters:', error);
            toast.error('Error clearing filters');
        }
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxPagesToShow = 5;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (
                let i = Math.max(2, currentPage - 1);
                i <= Math.min(totalPages - 1, currentPage + 1);
                i++
            ) {
                if (!pages.includes(i)) pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    return (
        <div className={`holiday-pay-layout ${className || ''}`}>
            <div className="holiday-pay-layout-main">
                <div className="invoice-table-container">
                    <div className="invoice-table-header">
                        <h3 className="invoice-table-title">{t('PENSION_TRANSACTION_HISTORY')}</h3>
                        <div className="holiday-filter-section">
                            <div className="holiday-filter-inputs">
                                <div className="holiday-filter-input" ref={fromCalendarRef}>
                                    <div className="holiday-filter-input-box" onClick={() => setShowFromCalendar(!showFromCalendar)}>
                                        <span className="holiday-filter-label">
                                            {fromDate ? fromDate.toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            }) : t('From')}
                                        </span>
                                        {toolTipError.fromDateNotSelected && (
                                            <div className="tooltip-error">
                                                {t('PLEASE_SELECT_FROM_DATE')}
                                            </div>
                                        )}
                                        {toolTipError.invalidFromdate && (
                                            <div className="tooltip-error">
                                                {t('FROM_DATE_CANNOT_BE_GREATER_THAN_TO_DATE')}
                                            </div>
                                        )}
                                        <div className="holiday-filter-icon">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                                <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                                <path d="M8 14H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M12 14H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M16 14H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M8 17H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M12 17H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M16 17H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                    {showFromCalendar && (
                                        <div className="calendar-dropdown">
                                            <div className="calendar-dropdown-header">
                                                <span>{t('SELECT_FROM_DATE')}</span>
                                                <button
                                                    type="button"
                                                    className="calendar-dropdown-close"
                                                    onClick={() => setShowFromCalendar(false)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            <CalendarSingle
                                                onDateSelect={(date) => {
                                                    setFromDate(date);
                                                    setShowFromCalendar(false);
                                                    setToolTipError({
                                                        invalidFromdate: false,
                                                        toDateNotSelected: false,
                                                        fromDateNotSelected: false,
                                                    });
                                                }}
                                                maxYear={new Date().getFullYear()}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="holiday-filter-input" ref={toCalendarRef}>
                                    <div className="holiday-filter-input-box" onClick={() => setShowToCalendar(!showToCalendar)}>
                                        <span className="holiday-filter-label">
                                            {toDate ? toDate.toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric'
                                            }) : t('To')}
                                        </span>
                                        {toolTipError.toDateNotSelected && (
                                            <div className="tooltip-error">
                                                {t('PLEASE_SELECT_TO_DATE')}
                                            </div>
                                        )}
                                        <div className="holiday-filter-icon">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <path d="M8 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                <path d="M16 2V5" stroke="#68727d" strokeWidth="1.5" strokeLinecap="round" />
                                                <rect x="3" y="4" width="18" height="15" rx="2" stroke="#68727d" strokeWidth="1.5" />
                                                <path d="M3 10H21" stroke="#68727d" strokeWidth="1.5" />
                                                <path d="M8 14H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M12 14H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M16 14H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M8 17H8.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M12 17H12.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                                <path d="M16 17H16.01" stroke="#68727d" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                        </div>
                                    </div>
                                    {showToCalendar && (
                                        <div className="calendar-dropdown">
                                            <div className="calendar-dropdown-header">
                                                <span>{t('SELECT_TO_DATE')}</span>
                                                <button
                                                    type="button"
                                                    className="calendar-dropdown-close"
                                                    onClick={() => setShowToCalendar(false)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                            <CalendarSingle
                                                onDateSelect={(date) => {
                                                    setToDate(date);
                                                    setShowToCalendar(false);
                                                    setToolTipError({
                                                        invalidFromdate: false,
                                                        toDateNotSelected: false,
                                                        fromDateNotSelected: false,
                                                    });
                                                }}
                                                maxYear={new Date().getFullYear()}
                                            />
                                        </div>
                                    )}
                                </div>
                                <button className="holiday-filter-clear-btn"
                                    onClick={handleClearFilters}
                                >
                                    {t('CLEAR')}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="invoice-table-content">
                        
                        {!isMobile && (
                            <TableHeader>
                                <TableColumn>{t('ID')}</TableColumn>
                                <TableColumn>{t('INVOICE_NUMBER')}</TableColumn>
                                <TableColumn>{t('PENSION_AMOUNT')}</TableColumn>
                                <TableColumn>{t('EMPLOYEE_NAME')}</TableColumn>
                                <TableColumn>{t('CREATED_DATE')}</TableColumn>
                            </TableHeader>
                        )}

                        <TableBody>
                            
                            {paginatedPensionRecords.length === 0 && (
                                <div className="invoice-table">
                                    {loading && (
                                <div >
                                <LoadingCard/></div>
                            )}
                                    <div className="invoice-table-content">
                                        {/* No drafts found message */}
                                        {(paginatedPensionRecords.length === 0) && (
                                            <div className='d-flex justify-content-center flex-column align-items-center pt-5'>
                                                <span className='pt-3'>{t('NO_PENSIONS_FOUND')}.</span>
                                                {error}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isMobile ? (
                                // Mobile: Card-based layout
                                paginatedPensionRecords?.map((pension: PensionRecord) => (
                                    <MobileCardWrapper key={pension.invoiceId || Math.random()}>
                                        <MobileCardRow label={t('ID')}>
                                            <span className="invoice-table-detailed__invoice-link">{pension.userId || 'N/A'}</span>
                                        </MobileCardRow>
                                        <MobileCardRow label={t('INVOICE_NUMBER')}>
                                            {pension.invoiceNumber ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('PENSION_AMOUNT')}>
                                            {formatAmount(pension.pensionAmount) ?? 'N/A'} {t('kr')}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('EMPLOYEE_NAME')}>
                                            {pension.employeeName ?? 'N/A'}
                                        </MobileCardRow>
                                        <MobileCardRow label={t('CREATED_DATE')}>
                                            {pension?.createdDate
                                                ? formatDateForPayout(pension.createdDate, false, language)
                                                : 'N/A'}
                                        </MobileCardRow>
                                    </MobileCardWrapper>
                                ))
                            ) : (
                                paginatedPensionRecords.map((pension: PensionRecord) => (
                                    <TableRow key={pension.invoiceId || Math.random()}>
                                        <TableCell>
                                            <span className="invoice-table-detailed__invoice-link">{pension.userId || 'N/A'}</span>
                                        </TableCell>
                                        <TableCell>{pension.invoiceNumber ?? 'N/A'}</TableCell>
                                        <TableCell>{formatAmount(pension.pensionAmount) ?? 'N/A'} {t('kr')}</TableCell>
                                        <TableCell>{pension.employeeName ?? 'N/A'}</TableCell>
                                        <TableCell>
                                            {pension?.createdDate
                                                ? formatDateForPayout(pension.createdDate, false, language)
                                                : 'N/A'}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </div>


                    {/* Pagination */}
                    {totalPages > 1 && paginatedPensionRecords && paginatedPensionRecords.length > 0 && (
                        <div className="invoice-table-pagination">
                            <div className="pagination-controls">
                                <button
                                    className="pagination-button pagination-button--prev"
                                    onClick={goToPreviousPage}
                                    disabled={currentPage === 1}
                                >
                                    <ArrowLeftIcon />
                                    <span>{t('PREV_STEP')}</span>
                                </button>

                                {getPageNumbers().map((page, index) => {
                                    if (page === '...') {
                                        return (
                                            <button
                                                key={`ellipsis-${index}`}
                                                className="pagination-button pagination-button--number"
                                                disabled
                                            >
                                                ...
                                            </button>
                                        );
                                    }
                                    return (
                                        <button
                                            key={page}
                                            className={`pagination-button pagination-button--number${page === currentPage ? ' pagination-button--active' : ''
                                                }`}
                                            onClick={() => setCurrentPage(page as number)}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}

                                <button
                                    className="pagination-button pagination-button--next"
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    <span>{t('NEXT_STEP')}</span>
                                    <ArrowRightIcon />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}